"use client";
import ApproveHeader from "../components/approveHeader";
import Button from "../components/ui/button";
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import axios from "axios";

const ApproveFunds = () => {
  const [percentageToDisburse, setPercentageToDisburse] = useState(60);
  const [amountDisbursed, setAmountDisbursed] = useState(0);
  const [amountRequested, setAmountRequested] = useState(0);
  const [offeredAmount, setOfferedAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLoanDetails = async () => {
      if (!id) return;
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        const response = axios(`/v1/admin/finance/single/${id}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (!response?.data?.error) {
          const data = await response.data;
          console.log("Fetched data:", data);

          const requestedAmount = data?.data?.loanAmount || 0;
          setAmountRequested(requestedAmount);

          const offered = data?.data?.requestedAmount || requestedAmount;
          setOfferedAmount(offered);

          const initialPercentage = Math.min(data?.data?.percentageToDisburse || 60, 60);
          setPercentageToDisburse(initialPercentage);
          setAmountDisbursed((requestedAmount * initialPercentage) / 100);
        } else {
          console.error("Failed to fetch loan details");
        }
      } catch (error) {
        console.error("Error fetching loan details:", error);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Error fetching data",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    };

    fetchLoanDetails();
  }, [id]);

  const handlePercentageChange = (value) => {
    const percentage = parseFloat(value);
    if (isNaN(percentage)) {
      setPercentageToDisburse(0);
      setAmountDisbursed(0);
      return;
    }

    if (percentage > 60) {
      setPercentageToDisburse(60);
      setAmountDisbursed((amountRequested * 60) / 100);
    } else {
      setPercentageToDisburse(percentage);
      setAmountDisbursed((amountRequested * percentage) / 100);
    }
  };

  const handleSubmit = async () => {
    if (percentageToDisburse > 60) {
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        offeredAmount: amountDisbursed,
        percentageToDisburse: percentageToDisburse / 100,
      };

      const token = localStorage.getItem("token") || sessionStorage.getItem("token");

      const response = await axios.post(`/v1/admin/new-finance/send-fund/${id}`, {
        ...payload,
      });

      if (!response?.data?.error) {
        const result = await response?.data;
        console.log("Success:", result);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Funds disbursed successfully!",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        navigate(-1);
      } else {
        const error = await response.data?.error;
        console.error("Error:", error);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: error?.response?.data?.message || error?.message || "Failed to disburse funds",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    } catch (error) {
      console.error("Network error:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: error?.response?.data?.message || error?.message || "Network error occurred",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="flex items-center justify-start w-full gap-10 px-6 py-4 border-b border-gray-200">
        <button className="p-1 hover:bg-gray-100 rounded-[8px]">
          <span
            className="material-icons"
            style={{ color: "#344054", cursor: "pointer" }}
            onClick={() => navigate(-1)}
          >
            close
          </span>
        </button>
        <h2 className="text-base font-medium text-gray-900">Approve request</h2>
      </div>
      <div className="flex justify-center">
        <div className="w-full max-w-[592px]">
          <Card className="border-none">
            <CardHeader className="pb-6">
              <CardTitle className="text-xl font-semibold text-gray-900">Disburse funds</CardTitle>
              <p className="text-sm text-[#667085] mt-1">Send approved funds to the customer</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-6">
                <div className="space-y-2">
                  <div className="text-sm font-bold text-black">Offered amount</div>
                  <div className="flex items-center rounded-[8px] bg-white px-3 py-2">
                    <span className="text-base font-medium text-black">
                      ₦{offeredAmount?.toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-bold text-black">Percentage to disburse</div>
                  <div className="flex items-center gap-3">
                    <div className="w-full">
                      <Input
                        type="number"
                        min="0"
                        max="60"
                        value={percentageToDisburse}
                        onChange={(e) => handlePercentageChange(e.target.value)}
                        className="w-full h-[46px] bg-gray-200 p-4 rounded-[10px]"
                      />
                    </div>
                    <div className="w-full h-[46px] bg-white rounded-[8px] px-3 py-2">
                      <span className="text-base font-medium text-black">
                        ₦{amountDisbursed.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-start pt-2">
                  <Button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="w-full rounded-[8px] md:w-auto px-8 py-3 bg-[#6F00FF] hover:bg-purple-700 text-white font-medium"
                    size="lg"
                  >
                    {isLoading ? "Disbursing..." : "Disburse funds"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ApproveFunds;
